import React from "react";
import { Link } from "react-router-dom";
import {
  HiArrowRight,
  HiChatBubbleLeftRight,
  HiChevronRight,
  HiCommandLine,
  HiCpuChip,
  HiSparkles,
  HiQuestionMarkCircle,
  HiBriefcase,
  HiLockClosed,
} from "react-icons/hi2";
import { ROUTES } from "../constants";
import TredningPostsm from "../components/TrendingPosts/TrendingPostsm";
import { trackCTAClick } from "../Actions/analytics";

// ─── Replace this with your actual Google Form link ───────────────────────────
const HIRING_FORM_URL = "https://forms.gle/8EdmtvhAZkAhWfzE7";
// ─────────────────────────────────────────────────────────────────────────────

const PublicHomePage = () => {
  return (
    <div className="flex flex-col min-h-screen bg-[#FAFAFA] selection:bg-sky-200 selection:text-sky-900 font-sans text-slate-900">

      {/* ── 1. HERO ────────────────────────────────────────────────────────── */}
      <section className="relative pt-32 pb-24 lg:pt-40 lg:pb-32 overflow-hidden bg-[#0A0F1C]">
        {/* Grid + radial gradient — unchanged */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#1e293b_1px,transparent_1px),linear-gradient(to_bottom,#1e293b_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)] opacity-20" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-3xl h-[400px] bg-sky-500/20 blur-[120px] rounded-full pointer-events-none" />

        <div className="max-w-7xl mx-auto px-6 relative z-10 text-center">

          {/* Live badge */}
          <Link
            to={ROUTES.login}
            className="inline-flex items-center gap-2 px-4 py-1.5 mb-8 text-xs font-bold text-sky-400 bg-sky-500/10 border border-sky-500/20 rounded-full hover:bg-sky-500/20 transition-all shadow-[0_0_15px_rgba(14,165,233,0.15)]"
          >
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-sky-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-sky-500" />
            </span>
            Live · Interview experiences from 50+ companies
            <HiChevronRight size={14} />
          </Link>

          {/* Headline — student-first, direct */}
          <h1 className="text-5xl md:text-7xl lg:text-[6rem] font-extrabold tracking-tighter leading-[1.05] mb-8 text-white">
            The placement intel your <br className="hidden md:block" />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-sky-400 via-indigo-400 to-purple-400">
              seniors kept to themselves.
            </span>
          </h1>

          {/* Subtitle — pain-point led */}
          <p className="text-lg md:text-xl text-slate-400 font-medium leading-relaxed max-w-3xl mx-auto mb-10">
            Verified round-by-round interview experiences from students who just cracked
            the same companies visiting your campus. Real questions, honest difficulty
            ratings, zero gatekeeping.
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-24">
            <Link
              to={ROUTES.posts}
              onClick={() => trackCTAClick('browse_experiences', ROUTES.posts)}
              className="w-full sm:w-auto flex items-center justify-center gap-2 bg-white text-slate-950 px-8 py-4 rounded-full font-bold text-base hover:scale-105 hover:bg-slate-100 transition-all shadow-[0_0_30px_rgba(255,255,255,0.1)]"
            >
              Browse Experiences <HiArrowRight />
            </Link>
            <a
              href="#ecosystem"
              className="w-full sm:w-auto flex items-center justify-center gap-2 bg-transparent text-white border border-slate-700 hover:border-slate-500 hover:bg-slate-800/50 px-8 py-4 rounded-full font-semibold text-base transition-all"
            >
              See the Roadmap
            </a>
          </div>

          {/* Dashboard mockup — structure unchanged */}
          <div className="relative mx-auto max-w-5xl group perspective-[2000px]">
            <div className="absolute -inset-1 bg-gradient-to-r from-sky-500 to-indigo-500 rounded-t-xl blur-2xl opacity-20 group-hover:opacity-30 transition-opacity duration-500" />
            <div className="relative rounded-t-xl bg-[#0F172A] border-t border-l border-r border-slate-700 shadow-2xl overflow-hidden flex flex-col items-center justify-start h-[300px] lg:h-[450px] transform transition-transform duration-700 hover:-translate-y-2">
              <div className="w-full bg-[#1E293B] border-b border-slate-700 px-4 py-3 flex items-center gap-2">
                <div className="flex gap-2">
                  <div className="w-3 h-3 rounded-full bg-slate-600/50" />
                  <div className="w-3 h-3 rounded-full bg-slate-600/50" />
                  <div className="w-3 h-3 rounded-full bg-slate-600/50" />
                </div>
                <div className="mx-auto h-5 w-64 bg-slate-800/50 rounded-md flex items-center justify-center text-[10px] text-slate-500 font-mono tracking-widest">
                  app.campusira.com
                </div>
              </div>
              <div className="w-full p-6 lg:p-10 opacity-70 pointer-events-none select-none">
                <TredningPostsm />
              </div>
              <div className="absolute bottom-0 left-0 w-full h-40 bg-gradient-to-t from-[#0A0F1C] to-transparent pointer-events-none" />
            </div>
          </div>
        </div>
      </section>

      {/* ── 2. PRODUCT ECOSYSTEM ──────────────────────────────────────────── */}
      <section id="ecosystem" className="py-32 bg-[#FAFAFA] scroll-mt-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-20 md:text-center max-w-3xl mx-auto">
            <h2 className="text-sm font-bold tracking-widest text-sky-600 uppercase mb-3">
              What we're building
            </h2>
            <h3 className="text-4xl md:text-5xl font-extrabold tracking-tight text-slate-900 mb-6">
              Not just another prep resource.
            </h3>
            <p className="text-slate-500 text-lg md:text-xl">
              From your first practice round to your final offer — CampusIRA is being
              built to give you the right information at the right time, verified by
              students who've been exactly where you are right now.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">

            {/* LIVE MODULE — Interview Intel */}
            <div className="group relative p-8 rounded-3xl bg-white border border-slate-200 hover:border-transparent shadow-sm hover:shadow-2xl hover:shadow-sky-500/10 transition-all duration-500 overflow-hidden hover:-translate-y-1">
              <div className="absolute inset-0 bg-gradient-to-br from-sky-400 via-blue-500 to-indigo-500 opacity-0 group-hover:opacity-100 transition-opacity duration-500 -z-20" />
              <div className="absolute inset-[1px] bg-white rounded-[23px] -z-10" />
              <div className="absolute -top-10 -right-10 w-40 h-40 bg-sky-500/10 blur-[40px] rounded-full -z-10 group-hover:scale-150 transition-transform duration-700" />

              <div className="w-14 h-14 bg-gradient-to-br from-sky-50 to-sky-100 border border-sky-200 rounded-2xl flex items-center justify-center text-sky-600 mb-8 shadow-inner transform group-hover:scale-110 transition-transform duration-300">
                <HiChatBubbleLeftRight size={26} />
              </div>
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-2xl font-bold text-slate-900">Interview Intel</h4>
                <span className="px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-black uppercase tracking-widest flex items-center gap-1.5 shadow-sm">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                  LIVE
                </span>
              </div>
              <p className="text-slate-600 mb-8 leading-relaxed">
                Round-by-round breakdowns from verified students who cracked Amazon,
                Google, Microsoft, and 50+ others. Know exactly what they asked at{" "}
                <em>your</em> college — not generic prep advice that applies to
                everyone and helps no one.
              </p>
              <Link
                to={ROUTES.posts}
                className="inline-flex items-center gap-2 text-sky-600 font-bold group-hover:gap-3 transition-all"
              >
                Browse Experiences <HiArrowRight />
              </Link>
            </div>

            {/* UPCOMING MODULES */}
            {[
              {
                icon: <HiBriefcase size={26} />,
                bgIcon: "from-emerald-50 to-emerald-100 border-emerald-200 text-emerald-600",
                glow: "from-emerald-400 to-teal-500",
                title: "Career Ops",
                status: "Rolling Out",
                desc: "Stop missing off-campus deadlines buried in LinkedIn. Curated job and internship alerts filtered by your branch, batch year, and college — plus alumni referral pipelines for companies that don't visit campuses directly.",
              },
              {
                icon: <HiSparkles size={26} />,
                bgIcon: "from-indigo-50 to-indigo-100 border-indigo-200 text-indigo-600",
                glow: "from-indigo-400 to-purple-500",
                title: "AI Mock Rounds",
                status: "In Alpha",
                desc: "Practice the exact question patterns companies use right now, powered by our verified experience database and Gemini AI. Get real feedback on your logic, communication, and time — before the actual interview.",
              },
              {
                icon: <HiCpuChip size={26} />,
                bgIcon: "from-purple-50 to-purple-100 border-purple-200 text-purple-600",
                glow: "from-purple-400 to-pink-500",
                title: "Resource Vault",
                status: "Indexing",
                desc: "Your senior's notes from 3 semesters ago are sitting in someone's Google Drive. We're fixing that. Structured, searchable, semester-wise academic resources — uploaded by students who actually scored well.",
              },
              {
                icon: <HiCommandLine size={26} />,
                bgIcon: "from-slate-100 to-slate-200 border-slate-300 text-slate-700",
                glow: "from-slate-400 to-slate-600",
                title: "Weekly Sprints",
                status: "Planning",
                desc: "Company-specific OA simulations built directly from our placement experience database. Companies reuse problems every year. We've documented them. Practice the ones that actually show up.",
              },
              {
                icon: <HiQuestionMarkCircle size={26} />,
                bgIcon: "from-orange-50 to-orange-100 border-orange-200 text-orange-600",
                glow: "from-orange-400 to-red-400",
                title: "Campus Q&A",
                status: "Planning",
                desc: "Is the Microsoft drive at your college for 3rd year or 4th year? What's the real shortlist cutoff? Ask anonymously, get verified answers from your own campus community — not Reddit strangers.",
              },
            ].map((module, idx) => (
              <div
                key={idx}
                className="group relative p-8 rounded-3xl bg-white border border-slate-200 hover:border-transparent transition-all duration-500 overflow-hidden hover:-translate-y-1 hover:shadow-xl"
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${module.glow} opacity-0 group-hover:opacity-100 transition-opacity duration-500 -z-20`} />
                <div className="absolute inset-[1px] bg-white rounded-[23px] -z-10" />
                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-8 shadow-sm border transform group-hover:scale-110 transition-transform duration-300 bg-gradient-to-br ${module.bgIcon}`}>
                  {module.icon}
                </div>
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-xl font-bold text-slate-800">{module.title}</h4>
                  <span className="px-2.5 py-1 rounded-full bg-slate-100 text-slate-500 text-[10px] font-bold uppercase tracking-wider flex items-center gap-1 border border-slate-200">
                    <HiLockClosed size={10} /> {module.status}
                  </span>
                </div>
                <p className="text-slate-500 leading-relaxed">{module.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 2.5 FEEDBACK BANNER ───────────────────────────────────────────── */}
      <section className="pb-24 bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="relative rounded-[2.5rem] overflow-hidden bg-slate-900 p-10 md:p-16 flex flex-col md:flex-row items-center justify-between gap-10 shadow-2xl">
            <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-sky-500/20 blur-[120px] rounded-full pointer-events-none translate-x-1/3 -translate-y-1/3" />
            <div className="absolute bottom-0 left-0 w-[300px] h-[300px] bg-indigo-500/20 blur-[100px] rounded-full pointer-events-none -translate-x-1/2 translate-y-1/2" />
            <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff0a_1px,transparent_1px),linear-gradient(to_bottom,#ffffff0a_1px,transparent_1px)] bg-[size:24px_24px] pointer-events-none" />

            <div className="relative z-10 max-w-2xl text-center md:text-left">
              <div className="inline-flex items-center gap-2 px-3 py-1 mb-6 text-xs font-bold text-sky-400 bg-sky-500/10 border border-sky-500/20 rounded-full">
                Built by students, for students
              </div>
              <h3 className="text-3xl md:text-4xl font-extrabold text-white mb-4 tracking-tight">
                Every feature started as a complaint{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-sky-400 to-indigo-400">
                  in a college WhatsApp group.
                </span>
              </h3>
              <p className="text-slate-400 text-lg md:text-xl leading-relaxed">
                CampusIRA exists because the information students needed was always
                there — just locked in a senior's head or a private chat. Have a
                feature request, spotted something broken, or know what's missing?
                Our team reads and responds to every single submission.
              </p>
            </div>

            <div className="relative z-10 shrink-0 w-full md:w-auto">
              <Link
                to="/help"
                className="w-full md:w-auto flex items-center justify-center gap-2 bg-white text-slate-950 px-8 py-5 rounded-full font-bold text-lg hover:scale-105 hover:bg-sky-50 transition-all shadow-[0_0_30px_rgba(255,255,255,0.15)] group"
              >
                Give Feedback
                <HiArrowRight className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── 3. SOCIAL PROOF / METRICS ─────────────────────────────────────── */}
      <section className="py-24 bg-slate-900 text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-sky-900/20 to-transparent pointer-events-none" />

        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="grid md:grid-cols-3 gap-8 divide-y md:divide-y-0 md:divide-x divide-slate-800">
            {[
              {
                val: "1,200+",
                label: "Students across India",
                sub: "And growing every placement season",
              },
              {
                val: "200+",
                label: "Verified placement experiences",
                sub: "With round-by-round breakdowns",
              },
              {
                val: "50+",
                label: "Companies documented",
                sub: "From product giants to service firms",
              },
            ].map((stat, i) => (
              <div
                key={i}
                className="py-8 md:py-0 md:px-8 text-center md:text-left first:pl-0 last:pr-0"
              >
                <div className="text-5xl font-black mb-2 tracking-tight text-transparent bg-clip-text bg-gradient-to-br from-white to-slate-400">
                  {stat.val}
                </div>
                <div className="text-sm font-semibold text-sky-400 uppercase tracking-widest mb-1">
                  {stat.label}
                </div>
                <div className="text-xs text-slate-500 font-medium">{stat.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 4. HIRING / JOIN THE TEAM ─────────────────────────────────────── */}
      <section className="py-24 bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="relative rounded-[2.5rem] overflow-hidden border border-slate-200 bg-white p-10 md:p-16 flex flex-col md:flex-row items-center justify-between gap-10 shadow-sm">

            {/* Decorative corner element */}
            <div className="absolute -top-20 -right-20 w-64 h-64 bg-gradient-to-br from-sky-100 to-indigo-100 rounded-full blur-3xl opacity-60 pointer-events-none" />

            <div className="relative z-10 max-w-2xl text-center md:text-left">
              {/* Hiring badge */}
              <div className="inline-flex items-center gap-2 px-3 py-1 mb-6 text-xs font-bold text-emerald-600 bg-emerald-50 border border-emerald-200 rounded-full">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                We're hiring interns
              </div>

              <h3 className="text-3xl md:text-4xl font-extrabold text-slate-900 mb-4 tracking-tight">
                Building CampusIRA is the internship.
              </h3>
              <p className="text-slate-500 text-lg leading-relaxed mb-6">
                We're a small team solving a real problem for 15 lakh engineering
                students every year. If you want to ship production code, own features
                end-to-end, and build something your entire campus will use — not just
                demo projects — there's a spot for you.
              </p>

              {/* Open roles */}
              <div className="flex flex-wrap gap-2">
                {[
                  "Frontend Dev",
                  "Backend Dev",
                  "Full Stack",
                  "UI/UX Design",
                  "AI Integration",
                  "Campus Growth",
                ].map((role) => (
                  <span
                    key={role}
                    className="px-3 py-1.5 rounded-full bg-slate-100 text-slate-600 text-xs font-semibold border border-slate-200"
                  >
                    {role}
                  </span>
                ))}
              </div>
            </div>

            {/* CTA — links to Google Form */}
            <div className="relative z-10 shrink-0 flex flex-col items-center gap-3 w-full md:w-auto">
              <Link
                to={ROUTES.hiring}
                onClick={() => trackCTAClick('apply_now_hiring', ROUTES.hiring)}
                className="w-full md:w-auto flex items-center justify-center gap-2 bg-slate-900 text-white px-8 py-5 rounded-full font-bold text-base hover:scale-105 hover:bg-slate-800 transition-all shadow-lg group"
              >
                Apply Now
                <HiArrowRight className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <p className="text-xs text-slate-400 font-medium text-center">
                Rolling applications · Respond within 7 days
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── 5. FOOTER ─────────────────────────────────────────────────────── */}
      <footer className="py-12 bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-slate-900 rounded-lg flex items-center justify-center text-white font-bold text-sm">
              C
            </div>
            <span className="font-bold text-xl tracking-tight text-slate-900">
              CampusIRA
            </span>
          </div>
          <div className="flex flex-wrap justify-center gap-6 text-sm font-semibold text-slate-500">
            <Link to={ROUTES.posts} className="hover:text-slate-900 transition-colors">
              Experiences
            </Link>
            <Link
              to={ROUTES.hiring}
              className="hover:text-slate-900 transition-colors flex items-center gap-1"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block" />
              Careers
            </Link>
            <a href="mailto:support@campusira.com" className="hover:text-slate-900 transition-colors">
              Support
            </a>
            <a href="mailto:admin@campusira.com" className="hover:text-slate-900 transition-colors">
              Contact
            </a>
          </div>
          <p className="text-sm text-slate-400 font-medium text-center md:text-right">
            © {new Date().getFullYear()} CampusIRA. Built in India, for India.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default PublicHomePage;